import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { resolveCssVar } from 'utils/css_var_utils';

const T = {
  bg: 'var(--vo-white)',
  border: 'var(--vo-line)',
  text: 'var(--vo-ink)',
  muted: 'var(--vo-slate)',
  dim: 'var(--vo-steel)',
};

export function RiskPipeline({ nodes = [], links = [], isLoading }) {
  const { t } = useTranslation(['scanner']);

  const option = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
      formatter: (params) => {
        if (params.dataType === 'edge') {
          return `${params.data.source} → ${params.data.target}: <b>${params.data.value.toLocaleString()}</b>`;
        }
        return `${params.data.name}: <b>${(params.data.value ?? 0).toLocaleString()}</b>`;
      },
    },
    series: [{
      type: 'sankey',
      data: nodes,
      links,
      orient: 'horizontal',
      nodeWidth: 16,
      nodeGap: 14,
      draggable: false,
      layoutIterations: 32,
      emphasis: { focus: 'adjacency' },
      label: {
        show: true,
        position: 'right',
        color: resolveCssVar(T.text),
        fontSize: 12,
        fontFamily: 'inherit',
        formatter: (params) =>
          `{name|${params.data.name}}\n{val|${(params.data.value ?? 0).toLocaleString()}}`,
        rich: {
          name: { fontSize: 12, color: resolveCssVar(T.text), fontWeight: 600, lineHeight: 18 },
          val:  { fontSize: 11, color: resolveCssVar(T.muted), lineHeight: 16 },
        },
      },
      lineStyle: {
        color: 'gradient',
        opacity: 0.45,
        curveness: 0.6,
      },
      left: '12%',
      right: '18%',
      top: 24,
      bottom: 24,
    }],
  }), [nodes, links]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 380 }}>
        <CircularProgress size={28} sx={{ color: 'var(--color-brand)' }} />
      </Box>
    );
  }

  if (nodes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 380 }}>
        <Typography sx={{ color: T.dim, fontSize: '0.9rem' }}>
          {t('scanner:dashboard.noData', 'No data')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <ReactECharts option={option} style={{ height: 380, width: '100%' }} notMerge />
    </Box>
  );
}
